import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { usePageMeta } from "../lib/meta";
import { Reveal } from "../components/Chrome";

const INTERESTS = ["PWA Apps", "Education", "AI Consultancy", "App Development", "General"];

export default function Contact() {
  usePageMeta("Contact — Klyx™", "Start a conversation with the Klyx™ studio.");
  const { data: content } = useQuery({
    queryKey: ["content", "contact"],
    queryFn: async () => (await api.get("/content/contact")).data,
  });
  const c = content?.data || { title: "Start a conversation", sub: "", email: "hello@klyxbs.com" };

  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "General", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Received. A partner will reply within two working days.");
      setForm({ name: "", email: "", company: "", interest: "General", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-ink border border-white/10 px-4 py-3 text-sm text-silver placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-mauve";

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 spotlight min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-mauve mb-6">Contact</p>
            <h1 data-testid="contact-title" className="font-heading font-black text-4xl sm:text-6xl tracking-tighter text-silver text-balance">
              {c.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-200 leading-relaxed max-w-md">{c.sub}</p>
            <p className="mt-10 text-sm text-zinc-300">
              Prefer email?{" "}
              <a href={`mailto:${c.email}`} data-testid="contact-email-link" className="text-mauve-light hover:text-white transition-colors duration-200">
                {c.email}
              </a>
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-7">
          <form data-testid="contact-form" onSubmit={submit} className="border border-white/10 bg-surface p-8 lg:p-12 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <input data-testid="contact-name-input" required value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} />
              <input data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
            </div>
            <input data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company (optional)" className={inputCls} />
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-300 mb-3">I am interested in</p>
              <div data-testid="contact-interest-pills" className="flex flex-wrap gap-2">
                {INTERESTS.map((i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`interest-pill-${i.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setForm({ ...form, interest: i })}
                    className={`px-4 py-2 text-xs tracking-[0.1em] uppercase border transition-[background-color,color,border-color] duration-200 ${
                      form.interest === i
                        ? "bg-mauve text-white border-mauve font-bold"
                        : "border-white/15 text-zinc-200 hover:border-mauve/50 hover:text-white"
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              data-testid="contact-message-input"
              required
              value={form.message}
              onChange={set("message")}
              rows={6}
              placeholder="Tell us about the project *"
              className={`${inputCls} resize-none`}
            />
            <button
              data-testid="contact-submit-button"
              disabled={sending}
              className="inline-flex items-center gap-3 bg-mauve text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold transition-[background-color,transform] duration-200 hover:bg-mauve-dark hover:-translate-y-0.5 disabled:opacity-40"
            >
              {sending ? "Sending…" : "Send enquiry"} <ArrowUpRight size={16} />
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
